import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import './App.css';
import { Home, Car, Layers, Flame, Phone, Mail, MapPin, Menu, X } from 'lucide-react';
import { Button } from './components/ui/button';
import { Card } from './components/ui/card';
import { Input } from './components/ui/input';
import { Textarea } from './components/ui/textarea';
import { Toaster } from './components/ui/sonner';
import { toast } from 'sonner';
import { api } from './services/api';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import AdminDashboard from './pages/AdminDashboard';
import Login from './pages/Login';
import Impressum from './pages/Impressum';
import Lightbox from './components/Lightbox';
import { usePageTracking } from './hooks/usePageTracking';

const iconMap = {
  Home: Home,
  Car: Car,
  Layers: Layers,
  Flame: Flame
};

const HomePage = () => {
  usePageTracking('/');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });
  
  // State for API data
  const [content, setContent] = useState({});
  const [services, setServices] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Lightbox state
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [currentProjectIndex, setCurrentProjectIndex] = useState(0);
  
  useEffect(() => {
    loadData();
  }, []);
  
  const loadData = async () => {
    try {
      const [contentRes, servicesRes, projectsRes] = await Promise.all([
        api.getAllContent(),
        api.getServices(),
        api.getProjects()
      ]);
      
      // Convert content array to object for easy access
      const contentObj = {};
      contentRes.data.forEach(item => {
        if (!contentObj[item.section]) contentObj[item.section] = {};
        contentObj[item.section][item.key] = item.value;
      });
      
      setContent(contentObj);
      setServices(servicesRes.data);
      setProjects(projectsRes.data);
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Fehler beim Laden der Daten');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await api.sendContactMessage(formData);
      toast.success('Nachricht erfolgreich gesendet! Wir melden uns bald bei Ihnen.');
      setFormData({ name: '', email: '', phone: '', message: '' });
    } catch (error) {
      console.error('Error sending message:', error);
      toast.error('Fehler beim Senden der Nachricht');
    }
  };

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      setMobileMenuOpen(false);
    }
  };
  
  const handleVideoClick = (e) => {
    const video = e.currentTarget.querySelector('video');
    if (video) {
      if (video.paused) {
        video.play();
        e.currentTarget.querySelector('.video-play-overlay').style.display = 'none';
      } else {
        video.pause();
        e.currentTarget.querySelector('.video-play-overlay').style.display = 'flex';
      }
    }
  };
  
  const openLightbox = (index) => {
    setCurrentProjectIndex(index);
    setLightboxOpen(true);
  };
  
  const closeLightbox = () => {
    setLightboxOpen(false);
  };
  
  const nextProject = () => {
    if (currentProjectIndex < projects.length - 1) {
      setCurrentProjectIndex(currentProjectIndex + 1);
    }
  };
  
  const prevProject = () => {
    if (currentProjectIndex > 0) {
      setCurrentProjectIndex(currentProjectIndex - 1);
    }
  };

  if (loading) {
    return (
      <div className="App">
        <div className="loading-screen">
          <div className="loading-spinner"></div>
          <p>Lädt...</p>
        </div>
      </div>
    );
  }
  
  const companyName = content.hero?.title || 'Gratl Montage';
  const companyTagline = content.hero?.tagline || 'Ihr Experte für Holzbau & Montage';
  const aboutTitle = content.about?.title || 'Über Uns';
  const aboutDescription = content.about?.description || '';
  const aboutValues = [
    content.about?.value1,
    content.about?.value2,
    content.about?.value3,
    content.about?.value4
  ].filter(Boolean);
  
  const contactPhone = content.contact?.phone || '';
  const contactEmail = content.contact?.email || '';
  const contactAddress = content.contact?.address || '';
  const servicesTitle = content.services?.title || 'Unsere Leistungen';
  const galleryTitle = content.gallery?.title || 'Unsere Projekte';
  const gallerySubtitle = content.gallery?.subtitle || '';
  const contactTitle = content.contact?.title || 'Kontakt';
  const contactSubtitle = content.contact?.subtitle || '';

  return (
    <div className="App">
      <Toaster position="top-center" />
      
      {/* Navigation */}
      <nav className="nav-bar">
        <div className="nav-container">
          <div className="nav-logo">{companyName}</div>
          
          {/* Desktop Navigation */}
          <div className="nav-links desktop-nav">
            <button onClick={() => scrollToSection('hero')} className="nav-link">Home</button>
            <button onClick={() => scrollToSection('about')} className="nav-link">Über Uns</button>
            <button onClick={() => scrollToSection('services')} className="nav-link">Leistungen</button>
            <button onClick={() => scrollToSection('gallery')} className="nav-link">Projekte</button>
            <button onClick={() => scrollToSection('contact')} className="nav-link">Kontakt</button>
            <a href="/impressum" className="nav-link">Impressum</a>
          </div>

          {/* Mobile Menu Button */}
          <button className="mobile-menu-btn" onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
            {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {/* Mobile Navigation */}
        {mobileMenuOpen && (
          <div className="mobile-nav">
            <button onClick={() => scrollToSection('hero')} className="nav-link">Home</button>
            <button onClick={() => scrollToSection('about')} className="nav-link">Über Uns</button>
            <button onClick={() => scrollToSection('services')} className="nav-link">Leistungen</button>
            <button onClick={() => scrollToSection('gallery')} className="nav-link">Projekte</button>
            <button onClick={() => scrollToSection('contact')} className="nav-link">Kontakt</button>
            <a href="/impressum" className="nav-link">Impressum</a>
          </div>
        )}
      </nav>

      {/* Hero Section */}
      <section id="hero" className="hero-section">
        <div className="hero-background">
          <img 
            src="https://images.unsplash.com/photo-1513467535987-fd81bc7d62f8?w=1920&h=1080&fit=crop" 
            alt="Holzbau" 
            className="hero-image"
          />
          <div className="hero-overlay"></div>
        </div>
        <div className="hero-content">
          <h1 className="hero-title">{companyName}</h1>
          <p className="hero-subtitle">{companyTagline}</p>
          <div className="hero-buttons">
            <Button className="btn-primary" onClick={() => scrollToSection('contact')}>
              Kontakt Aufnehmen
            </Button>
            <Button className="btn-secondary" onClick={() => scrollToSection('services')}>
              Unsere Leistungen
            </Button>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="about-section">
        <div className="section-container">
          <h2 className="section-title">{aboutTitle}</h2>
          <p className="about-description">{aboutDescription}</p>
          
          {/* Photo and Text Block 1 */}
          <div className="about-photo-section">
            <div className="about-photo-wrapper">
              {content.about?.team_photo ? (
                content.about?.team_photo_link ? (
                  <a
                    href={content.about.team_photo_link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="about-photo-link"
                    data-testid="about-team-photo-link"
                  >
                    <img 
                      src={content.about.team_photo} 
                      alt="Team Gratl-Montage" 
                      className="about-team-photo"
                    />
                  </a>
                ) : (
                  <img 
                    src={content.about.team_photo} 
                    alt="Team Gratl-Montage" 
                    className="about-team-photo"
                  />
                )
              ) : (
                <div className="about-photo-placeholder">
                  <div className="placeholder-icon">📷</div>
                  <p className="placeholder-text">Team-Foto hier einfügen</p>
                  <p className="placeholder-hint">(Im Admin-Bereich unter "Inhalte" → "ABOUT" → "team_photo" bearbeitbar)</p>
                </div>
              )}
            </div>
            {content.about?.team_description && (
              <div className="about-team-description">
                <p>{content.about.team_description}</p>
              </div>
            )}
          </div>
          
          {/* Photo and Text Block 2 - Additional */}
          <div className="about-photo-section about-additional-section">
            <div className="about-photo-wrapper">
              {content.about?.additional_photo ? (
                content.about?.additional_photo_link ? (
                  <a
                    href={content.about.additional_photo_link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="about-photo-link"
                    data-testid="about-additional-photo-link"
                  >
                    <img 
                      src={content.about.additional_photo} 
                      alt="Gratl-Montage Arbeit" 
                      className="about-team-photo"
                    />
                  </a>
                ) : (
                  <img 
                    src={content.about.additional_photo} 
                    alt="Gratl-Montage Arbeit" 
                    className="about-team-photo"
                  />
                )
              ) : (
                <div className="about-photo-placeholder">
                  <div className="placeholder-icon">📷</div>
                  <p className="placeholder-text">Zusätzliches Foto hier einfügen</p>
                  <p className="placeholder-hint">(Im Admin-Bereich unter "Inhalte" → "ABOUT" → "additional_photo" bearbeitbar)</p>
                </div>
              )}
            </div>
            {content.about?.additional_text && (
              <div className="about-team-description">
                <p>{content.about.additional_text}</p>
              </div>
            )}
          </div>
          
          <div className="values-grid">
            {aboutValues.map((value, index) => (
              <div key={index} className="value-card">
                <div className="value-bullet"></div>
                <p className="value-text">{value}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="services-section">
        <div className="section-container">
          <h2 className="section-title">{servicesTitle}</h2>
          <div className="services-grid">
            {services.map((service) => {
              const IconComponent = iconMap[service.icon] || Home;
              return (
                <Card key={service.id} className="service-card">
                  <div className="service-icon">
                    <IconComponent size={48} strokeWidth={1.5} />
                  </div>
                  <h3 className="service-title">{service.title}</h3>
                  <p className="service-description">{service.description}</p>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Gallery Section */}
      <section id="gallery" className="gallery-section">
        <div className="section-container">
          <h2 className="section-title">{galleryTitle}</h2>
          <p className="gallery-subtitle">{gallerySubtitle}</p>
          <div className="gallery-grid">
            {projects.map((project, index) => (
              <div 
                key={project.id} 
                className="gallery-item"
                data-testid={`gallery-item-${project.id}`}
              >
                <div className="gallery-media" onClick={() => openLightbox(index)}>
                  {project.media_type === 'video' ? (
                    <div className="video-wrapper">
                      <video 
                        src={project.image_url} 
                        className="gallery-image"
                        preload="metadata"
                      />
                      <div className="video-play-overlay">
                        <div className="play-button">
                          <svg width="64" height="64" viewBox="0 0 64 64" fill="none">
                            <circle cx="32" cy="32" r="32" fill="rgba(217, 251, 6, 0.9)" />
                            <path d="M26 20L44 32L26 44V20Z" fill="#1a1c1b" />
                          </svg>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <img src={project.image_url} alt={project.title} className="gallery-image" />
                  )}
                </div>
                {project.link_url ? (
                  <a
                    href={project.link_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="gallery-overlay gallery-overlay-link"
                    data-testid={`gallery-link-${project.id}`}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <p className="gallery-category">{project.category} ↗</p>
                    <h4 className="gallery-title">{project.title}</h4>
                  </a>
                ) : (
                  <div className="gallery-overlay" onClick={() => openLightbox(index)}>
                    <p className="gallery-category">{project.category}</p>
                    <h4 className="gallery-title">{project.title}</h4>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Lightbox */}
      {lightboxOpen && (
        <Lightbox
          project={projects[currentProjectIndex]}
          onClose={closeLightbox}
          onNext={nextProject}
          onPrev={prevProject}
          hasNext={currentProjectIndex < projects.length - 1}
          hasPrev={currentProjectIndex > 0}
        />
      )}

      {/* Contact Section */}
      <section id="contact" className="contact-section">
        <div className="section-container">
          <h2 className="section-title">{contactTitle}</h2>
          <div className="contact-grid">
            {/* Contact Info */}
            <div className="contact-info">
              <h3 className="contact-info-title">Kontaktieren Sie uns</h3>
              <p className="contact-info-text">{contactSubtitle}</p>
              
              <div className="contact-details">
                {contactPhone && (
                  <div className="contact-detail">
                    <Phone className="contact-icon" size={24} />
                    <div>
                      <p className="contact-label">Telefon</p>
                      <a href={`tel:${contactPhone}`} className="contact-value">{contactPhone}</a>
                    </div>
                  </div>
                )}
                
                {contactEmail && (
                  <div className="contact-detail">
                    <Mail className="contact-icon" size={24} />
                    <div>
                      <p className="contact-label">E-Mail</p>
                      <a href={`mailto:${contactEmail}`} className="contact-value">{contactEmail}</a>
                    </div>
                  </div>
                )}
                
                {contactAddress && (
                  <div className="contact-detail">
                    <MapPin className="contact-icon" size={24} />
                    <div>
                      <p className="contact-label">Standort</p>
                      <p className="contact-value">{contactAddress}</p>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Contact Form */}
            <div className="contact-form-wrapper">
              <form onSubmit={handleSubmit} className="contact-form">
                <div className="form-group">
                  <label htmlFor="name" className="form-label">Name</label>
                  <Input 
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    placeholder="Ihr Name"
                    required
                  />
                </div>
                
                <div className="form-group">
                  <label htmlFor="email" className="form-label">E-Mail</label>
                  <Input 
                    id="email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    placeholder="ihre@email.at"
                    required
                  />
                </div>
                
                <div className="form-group">
                  <label htmlFor="phone" className="form-label">Telefon</label>
                  <Input 
                    id="phone"
                    name="phone"
                    type="tel"
                    value={formData.phone}
                    onChange={handleInputChange}
                    placeholder="Ihre Telefonnummer"
                  />
                </div>
                
                <div className="form-group">
                  <label htmlFor="message" className="form-label">Nachricht</label>
                  <Textarea 
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleInputChange}
                    placeholder="Beschreiben Sie Ihr Projekt..."
                    rows={5}
                    required
                  />
                </div>
                
                <Button type="submit" className="btn-primary btn-full-width">
                  Nachricht Senden
                </Button>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="footer">
        <div className="footer-container">
          <div className="footer-content">
            <div className="footer-brand">
              <h3 className="footer-logo">{companyName}</h3>
              <p className="footer-tagline">{companyTagline}</p>
            </div>
            <div className="footer-contact">
              {contactPhone && <p>Tel: {contactPhone}</p>}
              {contactEmail && <p>Email: {contactEmail}</p>}
            </div>
          </div>
          <div className="footer-bottom">
            <p>&copy; 2024 {companyName}. Alle Rechte vorbehalten.</p>
            <div className="footer-links">
              <a href="/impressum" className="footer-link">Impressum</a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return (
      <div className="App">
        <div className="loading-screen">
          <div className="loading-spinner"></div>
          <p>Lädt...</p>
        </div>
      </div>
    );
  }

  return isAuthenticated ? children : <Navigate to="/login" replace />;
};

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/login" element={<Login />} />
          <Route path="/impressum" element={<Impressum />} />
          <Route 
            path="/admin" 
            element={
              <ProtectedRoute>
                <AdminDashboard />
              </ProtectedRoute>
            } 
          />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;